import React from "react";
import { Info, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "react-toastify";

const STATBEL_URL = "https://statbel.fgov.be/nl/themas/consumptieprijsindex/huurcalculator";

interface IndexedRentNoticeProps {
  signDate: Date | null;
  startDate: Date | null;
  /** Current EPC of the unit — its label drives the Statbel energiecorrectie. */
  epc?: { label: string; kWh: string } | null;
  products: Array<{ name: string; basePrice: number }>;
}

const formatDate = (date: Date | null): string =>
  date ? new Date(date).toLocaleDateString("nl-BE") : "—";

const CopyChip: React.FC<{
  label: string;
  value: string;
  display?: string;
  disabled?: boolean;
}> = ({ label, value, display, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => {
      navigator.clipboard?.writeText(value);
      toast.success(`${label} gekopieerd`);
    }}
    className={`inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs transition-colors ${
      disabled ? "cursor-not-allowed opacity-50" : "hover:border-amber-400 hover:bg-amber-50"
    }`}
    title={`Kopieer ${label}`}
  >
    <span className="text-neutral-secondary-mid">{label}:</span>
    <span className="font-medium text-neutral-primary">{display ?? value}</span>
    <Copy size={13} className="text-amber-700" />
  </button>
);

/**
 * The indexed rent ("geïndexeerde huur") is not stated in the base contract —
 * only the aanvangshuurprijs is. So the prefilled indexed value equals the base
 * price and must be recomputed with the official Statbel rent calculator. This
 * notice links to it and lets the user one-click-copy the inputs it needs.
 */
const IndexedRentNotice: React.FC<IndexedRentNoticeProps> = ({ signDate, startDate, epc, products }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5">
    <div className="flex items-start gap-2">
      <Info size={18} className="mt-0.5 shrink-0 text-amber-700" weight="fill" />
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-amber-900">
            Geïndexeerde huur is niet automatisch berekend
          </p>
          <p className="text-xs text-amber-800">
            De geïndexeerde huur staat niet in het basiscontract en is daarom bewust leeg gelaten.
            Bereken hem met de Statbel-huurcalculator en vul het veld "Geïndexeerd / maand" hierboven in.
          </p>
        </div>

        <a
          href={STATBEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
        >
          Open Statbel-huurcalculator
          <ArrowSquareOut size={13} />
        </a>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-amber-800">Kopieer om in te vullen:</span>
          <CopyChip label="Ondertekeningsdatum" value={formatDate(signDate)} disabled={!signDate} />
          <CopyChip label="Startdatum" value={formatDate(startDate)} disabled={!startDate} />
          {epc?.label && (
            <CopyChip
              label="EPC"
              value={epc.label}
              display={epc.kWh ? `${epc.label} (${epc.kWh} kWh/m²)` : epc.label}
            />
          )}
          {products
            .filter((p) => p.basePrice > 0)
            .map((p, i) => (
              <CopyChip
                key={i}
                label={`Basishuur ${p.name || ""}`.trim()}
                value={String(p.basePrice)}
              />
            ))}
        </div>
      </div>
    </div>
  </div>
);

export default IndexedRentNotice;
